import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import listingPerformanceApi from '../services/listingPerformanceApi';

// «أداء الوحدات» (tasks/property-performance-phase1.md step 5). Every figure, verdict and colour is a
// SERVER answer — the page never computes one. Each write invalidates the page's queries rather than
// patching a value locally (the useStock rule).

export const LISTING_PERF_KEY = ['listing-performance'];

/**
 * Keep the previous answer on screen (dimmed) ONLY when the week alone changed. A different owner or
 * view must never show the last one's rows meanwhile — owner A's units under owner B, or staff-only
 * colours under the owner view.
 */
const keepIfSameScope = (ownerId, view) => (previous, previousQuery) => {
  const key = previousQuery?.queryKey || [];
  return key[2] === (ownerId || null) && key[4] === (view || 'staff') ? previous : undefined;
};

/** The whole board. */
export const usePerformanceBoard = ({ ownerId, week, view }, enabled = true) => useQuery({
  queryKey: [...LISTING_PERF_KEY, 'board', ownerId || null, week || null, view || 'staff'],
  queryFn: () => listingPerformanceApi.getBoard({ ownerId, week, view }),
  enabled,
  staleTime: 60 * 1000,
  placeholderData: keepIfSameScope(ownerId, view)
});

export const usePerformanceUnit = ({ unitCode, ownerId, week, view }) => useQuery({
  queryKey: [...LISTING_PERF_KEY, 'unit', unitCode || null, ownerId || null, week || null, view || 'staff'],
  queryFn: () => listingPerformanceApi.getUnit({ unitCode, ownerId, week, view }),
  enabled: Boolean(unitCode),
  staleTime: 30 * 1000,
  // A reading asked for is written within about a minute (the run waits 30 s for a burst of uploads).
  refetchInterval: (query) => (query.state.data?.brief?.status === 'pending' ? 15 * 1000 : false),
  // Same unit, same owner, same view — only then may the old answer stay up while the week changes.
  placeholderData: (previous, previousQuery) => {
    const key = previousQuery?.queryKey || [];
    return key[2] === (unitCode || null) && key[3] === (ownerId || null) && key[5] === (view || 'staff') ? previous : undefined;
  }
});

export const usePerformanceThresholds = (enabled) => useQuery({
  queryKey: [...LISTING_PERF_KEY, 'thresholds'],
  queryFn: () => listingPerformanceApi.getThresholds(),
  enabled: Boolean(enabled),
  staleTime: 60 * 1000
});

export const usePerformanceKeys = (enabled) => useQuery({
  queryKey: [...LISTING_PERF_KEY, 'keys'],
  queryFn: () => listingPerformanceApi.getKeys(),
  enabled: Boolean(enabled),
  staleTime: 30 * 1000
});

export const usePerformanceSubmissions = (enabled) => useQuery({
  queryKey: [...LISTING_PERF_KEY, 'submissions'],
  queryFn: () => listingPerformanceApi.getSubmissions(),
  enabled: Boolean(enabled),
  staleTime: 30 * 1000
});

// Retry once, and ONLY when no answer came back — a refusal is final (the useStock rule). The writes set
// values, so doing one twice changes nothing; creating a key is the exception and is never retried.
const retryOnlyWithoutAnswer = (failureCount, error) => !error?.response && failureCount < 1;

const useAction = (mutationFn, { retry = retryOnlyWithoutAnswer } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    retry,
    onSettled: () => queryClient.invalidateQueries({ queryKey: LISTING_PERF_KEY })
  });
};

export const useCreateChange = () => useAction(listingPerformanceApi.createChange, { retry: false });
export const useReviewBrief = () => useAction(listingPerformanceApi.reviewBrief);
export const useRerunBrief = () => useAction(listingPerformanceApi.rerunBrief);
export const useUpdateChange = () => useAction(listingPerformanceApi.updateChange);
export const useSaveThresholds = () => useAction(listingPerformanceApi.saveThresholds, { retry: false });
export const useCreateKey = () => useAction(listingPerformanceApi.createKey, { retry: false });
export const useRevokeKey = () => useAction(listingPerformanceApi.revokeKey);
